import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Settings as SettingsIcon,
  User,
  Moon,
  Sun,
  Shield,
  LogOut,
  Building,
  Loader2,
  Check,
  AlertTriangle,
  Plus,
  X,
  Globe,
  Database,
  Trash2,
  HardDrive,
  HelpCircle,
} from "lucide-react";
import { resetTour } from "@/components/GuidedTour";

export default function Settings() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [newDomain, setNewDomain] = useState("");
  const [domains, setDomains] = useState([]);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  
  // Data management state
  const [retentionSettings, setRetentionSettings] = useState(null);
  const [storageStats, setStorageStats] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [audioRetention, setAudioRetention] = useState(60);
  const [transcriptRetention, setTranscriptRetention] = useState(365);
  const [maxStorage, setMaxStorage] = useState(500);

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await fetch(`${API}/auth/domains`, { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          setDomains(data.allowed_domains || []);
        }
      } catch (error) {
        console.error("Failed to fetch domains:", error);
      }
    };
    fetchDomains();
  }, []);

  // Fetch data management settings
  useEffect(() => {
    if (!user?.is_admin && !user?.is_manager) return;
    const fetchDataSettings = async () => {
      try {
        const [settingsRes, statsRes] = await Promise.all([
          fetch(`${API}/data-management/settings`, { credentials: "include" }),
          fetch(`${API}/data-management/storage-stats`, { credentials: "include" })
        ]);
        if (settingsRes.ok) {
          const s = await settingsRes.json();
          setRetentionSettings(s);
          setAudioRetention(s.audio_retention_days);
          setTranscriptRetention(s.transcript_retention_days);
          setMaxStorage(s.max_storage_per_user_mb);
        }
        if (statsRes.ok) setStorageStats(await statsRes.json());
      } catch (e) { console.warn("Failed to fetch data-management settings:", e); }
    };
    fetchDataSettings();
  }, [user]);

  const handleSaveRetention = async () => {
    setRetentionSaving(true);
    try {
      const res = await fetch(`${API}/data-management/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          audio_retention_days: audioRetention,
          transcript_retention_days: transcriptRetention,
          max_storage_per_user_mb: maxStorage
        })
      });
      if (res.ok) toast.success("Retention settings saved");
      else toast.error("Failed to save settings");
    } catch { toast.error("Failed to save settings"); }
    finally { setRetentionSaving(false); }
  };

  const handleRunCleanup = async () => {
    setCleanupLoading(true);
    try {
      const res = await fetch(`${API}/data-management/cleanup`, {
        method: "POST", credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Cleanup complete: freed ${data.freed_mb}MB`, {
          description: `Deleted ${data.deleted_audio} recordings, ${data.deleted_transcripts} old sessions`
        });
        // Refresh stats
        const statsRes = await fetch(`${API}/data-management/storage-stats`, { credentials: "include" });
        if (statsRes.ok) setStorageStats(await statsRes.json());
      } else toast.error("Cleanup failed");
    } catch { toast.error("Cleanup failed"); }
    finally { setCleanupLoading(false); }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error("Please enter a domain");
      return;
    }

    // Validate domain format
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newDomain.trim())) {
      toast.error("Invalid domain format", {
        description: "Example: company.com"
      });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/auth/add-domain`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ allowed_domain: newDomain.trim().toLowerCase() })
      });

      if (response.ok) {
        const data = await response.json();
        setDomains(data.domains);
        setNewDomain("");
        toast.success("Domain added", {
          description: `@${newDomain.trim()} users can now sign in`
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add domain");
      }
    } catch (error) {
      console.error("Failed to add domain:", error);
      toast.error("Failed to add domain");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDomain = async (domain) => {
    setRemoving(domain);
    try {
      const response = await fetch(`${API}/auth/remove-domain`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ allowed_domain: domain })
      });

      if (response.ok) {
        const data = await response.json();
        setDomains(data.domains);
        toast.success("Domain removed", {
          description: `@${domain} users can no longer sign in`
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to remove domain");
      }
    } catch (error) {
      console.error("Failed to remove domain:", error);
      toast.error("Failed to remove domain");
    } finally {
      setRemoving(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                {user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-lg">{user?.name}</h3>
                {user?.is_admin && (
                  <Badge variant="default" className="bg-amber-500">Admin</Badge>
                )}
                {user?.is_manager && !user?.is_admin && (
                  <Badge variant="secondary">Manager</Badge>
                )}
              </div>
              <p className="text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {theme === "dark" ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Dark Mode</p>
              <p className="text-sm text-muted-foreground">Toggle between light and dark theme</p>
            </div>
            <Switch
              checked={theme === "dark"}
              onCheckedChange={toggleTheme}
              data-testid="theme-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Guided Tour */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            Guided Tour
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Restart Tour</p>
              <p className="text-sm text-muted-foreground">Walk through the app features step by step</p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                resetTour();
                navigate("/dashboard");
                toast.success("Tour restarted! Redirecting to dashboard...");
              }}
              data-testid="restart-tour-settings-btn"
            >
              <HelpCircle className="w-4 h-4 mr-2" /> Start Tour
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Organization Settings - Admin Only */}
      {user?.is_admin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Organization Access
            </CardTitle>
            <CardDescription>
              Manage which email domains can sign in to this application
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Domains */}
            {domains.length > 0 ? (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="font-medium text-green-600 dark:text-green-400">
                    Domain restrictions active
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {domains.map((domain) => (
                    <Badge 
                      key={domain} 
                      variant="secondary" 
                      className="pl-3 pr-1 py-1.5 flex items-center gap-2"
                    >
                      <Globe className="w-3 h-3" />
                      @{domain}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 hover:bg-destructive/20 hover:text-destructive rounded-full"
                        onClick={() => handleRemoveDomain(domain)}
                        disabled={removing === domain}
                        data-testid={`remove-domain-${domain}`}
                      >
                        {removing === domain ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                      </Button>
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Only users with email addresses from these domains can sign in
                </p>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  <span className="font-medium text-yellow-600 dark:text-yellow-400">
                    No domain restrictions
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Anyone with a Google account can sign in. Add domains to restrict access.
                </p>
              </div>
            )}

            <Separator />

            {/* Add New Domain */}
            <div className="space-y-2">
              <Label htmlFor="domain">Add Allowed Domain</Label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                  <Input
                    id="domain"
                    placeholder="company.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
                    className="pl-8"
                    data-testid="domain-input"
                  />
                </div>
                <Button 
                  onClick={handleAddDomain} 
                  disabled={saving}
                  data-testid="add-domain-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Add multiple domains to allow users from different organizations
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Non-admin view of domains */}
      {!user?.is_admin && domains.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Organization Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg bg-muted">
              <p className="text-sm text-muted-foreground mb-2">Allowed domains:</p>
              <div className="flex flex-wrap gap-2">
                {domains.map((domain) => (
                  <Badge key={domain} variant="secondary">
                    <Globe className="w-3 h-3 mr-1" />
                    @{domain}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Management - Admin/Manager only */}
      {(user?.is_admin || user?.is_manager) && (
        <Card data-testid="data-management-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Data Management
            </CardTitle>
            <CardDescription>
              Configure retention policies and manage storage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Storage Stats */}
            {storageStats && (
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <HardDrive className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Storage Usage</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Storage</span>
                    <p className="font-mono font-medium">{(storageStats.total_storage_bytes / (1024 * 1024)).toFixed(1)} MB</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Recordings</span>
                    <p className="font-mono font-medium">{storageStats.total_recordings}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Retention Settings - Admin only */}
            {user?.is_admin && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h4 className="text-sm font-medium">Retention Policy</h4>
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="audio-retention">Audio Recordings (days)</Label>
                      <Input
                        id="audio-retention"
                        type="number"
                        min={1}
                        value={audioRetention}
                        onChange={(e) => setAudioRetention(parseInt(e.target.value) || 1)}
                        data-testid="audio-retention-input"
                      />
                      <p className="text-xs text-muted-foreground">Recordings older than this will be auto-deleted</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="transcript-retention">Transcripts & Scores (days)</Label>
                      <Input
                        id="transcript-retention"
                        type="number"
                        min={1}
                        value={transcriptRetention}
                        onChange={(e) => setTranscriptRetention(parseInt(e.target.value) || 1)}
                        data-testid="transcript-retention-input"
                      />
                      <p className="text-xs text-muted-foreground">Session data older than this will be removed (pinned sessions are exempt)</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-storage">Max Storage Per User (MB)</Label>
                      <Input
                        id="max-storage"
                        type="number"
                        min={10}
                        value={maxStorage}
                        onChange={(e) => setMaxStorage(parseInt(e.target.value) || 10)}
                        data-testid="max-storage-input"
                      />
                      <p className="text-xs text-muted-foreground">Oldest recordings auto-deleted when user exceeds quota</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveRetention} disabled={retentionSaving} data-testid="save-retention-btn">
                      {retentionSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                      Save Settings
                    </Button>
                    <Button variant="destructive" onClick={handleRunCleanup} disabled={cleanupLoading} data-testid="run-cleanup-btn">
                      {cleanupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                      Run Cleanup Now
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sign Out */}
      <Card className="border-destructive/50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Sign Out</p>
              <p className="text-sm text-muted-foreground">Sign out of your account</p>
            </div>
            <Button 
              variant="destructive" 
              onClick={handleLogout}
              data-testid="signout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
